import { useRef, useState, type ReactNode } from 'react';

/**
 * An iOS sheet for the phone editor (spec 14.4): attached to the bottom,
 * rounded top corners, a grabber, and a 44 nav bar with the title centred
 * between a leading and a trailing control. The grabber and the nav bar can
 * be dragged down: past 90 the sheet is dismissed, short of it it springs
 * back (SH-4, SH-5). The content scrolls inside, with overscroll contained.
 *
 * `size` 'fit' sizes to the content (the compare list); 'large' runs from
 * the top safe area (the browse lists). In landscape a 'fit' sheet is 360
 * wide and centred, a 'large' one up to 560.
 *
 * Top radius 34: Apple's Sheet/iPhone/Top Radius token (DESIGN_SYSTEM FM-6,
 * measured off an iPad frame, see FM-0).
 */
export function IosSheet({
  title, leading, trailing, onDismiss, size = 'fit', landscape, children, cardRef, testId, footer, bare = false, dismissing = false,
}: {
  /** Pinned under the scrolling content (an Open button). */
  footer?: ReactNode;
  /** No nav bar: the grabber alone, with the content directly under it (the drill sheet, 15.24). */
  bare?: boolean;
  /** Sliding out (200ms) before it unmounts. */
  dismissing?: boolean;
  title: string;
  leading?: ReactNode;
  trailing?: ReactNode;
  onDismiss: () => void;
  size?: 'fit' | 'large';
  landscape: boolean;
  children: ReactNode;
  cardRef?: React.Ref<HTMLDivElement>;
  testId?: string;
}) {
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef<{ id: number; y: number } | null>(null);

  const width = landscape ? (size === 'large' ? 'min(560px, calc(100vw - 48px))' : '360px') : undefined;
  return (
    <div
      ref={cardRef}
      role="dialog"
      aria-label={title}
      data-testid={testId}
      className="fixed flex flex-col overflow-hidden"
      style={{
        zIndex: 'var(--z-editor-menu)',
        bottom: 0,
        left: landscape ? '50%' : 0,
        right: landscape ? undefined : 0,
        width,
        top: size === 'large' ? (landscape ? 'var(--lve-edge-top, 12px)' : 'calc(env(safe-area-inset-top, 0px) + 10px)') : undefined,
        maxHeight: size === 'fit' ? 'calc(var(--vv-height, 100dvh) - env(safe-area-inset-top, 0px) - 10px)' : undefined,
        transform: `${landscape ? 'translateX(-50%) ' : ''}translateY(${dismissing ? '110%' : `${dragY}px`})`,
        transition: dragging ? 'none' : 'transform 200ms ease',
        background: 'var(--editor-sidebar)',
        borderTopLeftRadius: 34,
        borderTopRightRadius: 34,
        boxShadow: '0 -8px 40px rgba(0,0,0,0.45)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div
        className="flex-none select-none"
        style={{ touchAction: 'none' }}
        onPointerDown={(e) => {
          if ((e.target as Element).closest('button, a, input')) return;
          startRef.current = { id: e.pointerId, y: e.clientY };
          e.currentTarget.setPointerCapture(e.pointerId);
          setDragging(true);
        }}
        onPointerMove={(e) => {
          const s = startRef.current;
          if (!s || s.id !== e.pointerId) return;
          setDragY(Math.max(0, e.clientY - s.y));
        }}
        onPointerUp={(e) => {
          const s = startRef.current;
          if (!s || s.id !== e.pointerId) return;
          startRef.current = null;
          setDragging(false);
          if (e.clientY - s.y > 90) onDismiss();
          else setDragY(0);
        }}
        onPointerCancel={() => { startRef.current = null; setDragging(false); setDragY(0); }}
      >
        <div className="flex justify-center pt-[5px] pb-[6px]" aria-hidden="true">
          <span className="block rounded-full" style={{ width: 36, height: 5, background: 'rgba(255,255,255,0.3)' }} />
        </div>
        {/* Nav bar, 44: leading, the title (Headline 17/22) centred, trailing. */}
        {!bare && (
          <div className="grid items-center px-2" style={{ height: 44, gridTemplateColumns: '1fr auto 1fr' }}>
            <div className="flex items-center justify-start min-w-0">{leading}</div>
            <h2 className="min-w-0 px-2 text-center text-[17px] leading-[22px] font-semibold [overflow-wrap:anywhere]" style={{ color: 'var(--editor-text)' }}>{title}</h2>
            <div className="flex items-center justify-end min-w-0">{trailing}</div>
          </div>
        )}
      </div>
      {/* Bare: the content lays itself out and scrolls its own part. */}
      <div className={`flex-1 min-h-0 ${bare ? 'flex flex-col overflow-hidden' : 'overflow-y-auto overscroll-contain pt-1 pb-3'}`}>{children}</div>
      {footer && <div className="flex-none px-4 pt-3 pb-2" style={bare ? undefined : { borderTop: '1px solid var(--editor-border)' }}>{footer}</div>}
    </div>
  );
}

/** A text button for a sheet's nav bar ("Cancel"), Body 17/22, in the editor's tint. */
export function SheetTextButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative h-11 px-2 text-[17px] leading-[22px] ios-press focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 rounded-lg"
      style={{ color: 'var(--color-db-oasis)' }}
    >
      {label}
    </button>
  );
}
